import React from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { A } from '@osmosis/tailwind-ui/components/Text';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { PROD_MEDIA_SERVER, CLOUDFRONT_ASSET_URL } from '@osmosis/constants';
import { RecommendedConcept } from '@osmosis/shared/types/pages/answerPages';
import { useIntl } from 'react-intl';
import Image from 'next/image';
import ItemThumbnail from './video/ItemThumbnail';
import { QuizRecommendedConcept } from '../types/quiz';
import { text3 } from '@osmosis/tailwind-ui/styles/text';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';

type VideoListConcept = RecommendedConcept | QuizRecommendedConcept;

type VideProps = {
  videos: VideoListConcept[];
  testId: string;
  isColumnLayout?: boolean;
  customClasses?: {
    [key: string]: string;
  };
};

/**
 * This is the Video component.
 * @returns  The Video component.
 */
const VideoList = ({
  videos,
  isColumnLayout = true,
  testId,
  customClasses,
}: VideProps) => {
  const { formatMessage } = useIntl();

  const uniqueVideos: VideoListConcept[] = videos
    .filter((video: VideoListConcept) => {
      return !video.tags.some((tag) => tag.tag === 'To be retired');
    })
    .reduce((acc: VideoListConcept[], video: VideoListConcept) => {
      if (!acc.some((v) => v.pageName === video.pageName)) {
        acc.push(video);
      }
      return acc;
    }, []);

  const customLinkClasses = customClasses?.linkClasses || '';
  const customTextClasses = customClasses?.textClasses || '';
  const linkClasses = `flex${
    isColumnLayout ? '' : ' flex-col w-[150px] flex-shrink-0'
  } ${customLinkClasses}`;
  const getVideoClasses = (video: VideoListConcept) =>
    `${video.smallThumb && isColumnLayout ? ' w-5/12' : ''}${
      !video.smallThumb && !isColumnLayout ? ' w-1/2' : ''
    }`;

  return (
    <Flex
      className={`flex-${isColumnLayout ? 'col' : 'row'} overflow-auto`}
      data-testid={testId}
    >
      {uniqueVideos.map(
        (video) =>
          video && (
            <A
              key={video.learnNodeID}
              href={
                video.url.startsWith('/learn')
                  ? video.url
                  : `/learn/${video.url}`
              }
              className={`${linkClasses}mb-4 group rounded-lg p-2 no-underline hover:bg-gray-200 focus:bg-gray-200 focus:text-blue-600`}
              aria-label={formatMessage({
                id: 'related.item.thumbnail.aria-label-title',
                defaultMessage: `Watch a video about ${video.pageName}`,
              })}
            >
              <Box
                className={`relative mr-4${getVideoClasses(
                  video,
                )} h-20 max-w-[130px] flex-shrink-0`}
              >
                {video.smallThumb ? (
                  <ItemThumbnail
                    thumbnailURL={`${
                      /^http/.test(video.smallThumb) ? '' : PROD_MEDIA_SERVER
                    }${video.smallThumb}`}
                    title={video.pageName}
                    minHeight="87px"
                    playIcon={true}
                    duration={video.duration}
                    dataTestId={`related-video-${video.learnNodeID}`}
                  />
                ) : (
                  <div
                    className={`h-full w-full rounded-md border-2 border-blue-600 object-cover ${bg0}`}
                    data-testId={`related-note-${video.learnNodeID}`}
                  >
                    <Image
                      height={200}
                      width={200}
                      src={`${CLOUDFRONT_ASSET_URL}8qFwkjZxST_hvmR9ywfHQLejT1WcvKZj.svg`}
                      alt={video.pageName}
                      className={`h-full w-full rounded-md object-cover opacity-25`}
                    />
                  </div>
                )}
              </Box>

              <P
                className={`font-bold ${text3} overflow-hidden break-words group-hover:text-blue-600${customTextClasses}`}
              >
                {video.pageName}
              </P>
            </A>
          ),
      )}
    </Flex>
  );
};

export default VideoList;
